import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress, Grid, Alert, AlertTitle, List, ListItem } from "@mui/material";
import TitleForm from "../../../common/components/titleform";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import colors from "../../../common/styles/colors";
import { useTranslation } from "react-i18next";
import DebriefingVideo from "../debriefing-video/DebriefingVideo";
import { ResponsiveRadar } from "@nivo/radar";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useSocket } from "../../../services/hooks/socketHook";
import SheAiFexService from "../../../services/sheai/SheAiFexService";
import secureLocalStorage from "react-secure-storage";

interface FexModuleProps {
  status: string;
  styles: any;
  video: string;
  videoId: string;
  arrVideo: any[];
  setVideo: (v: string) => void;
  setVideoId: (v: string) => void;
  cameraMain: boolean;
  setCameraMain: (v: boolean) => void;
  setLoader: (v: boolean) => void;
  idEvaluated: any;
  idDebriefing: any;
  faceRecognition?: boolean;
}

const FexModule: React.FC<FexModuleProps> = ({
  status,
  styles,
  video,
  videoId,
  arrVideo,
  setVideo,
  setVideoId,
  cameraMain,
  setCameraMain,
  setLoader,
  idEvaluated,
  idDebriefing,
  faceRecognition,
}) => {
  const { t } = useTranslation();
  const [wsProgressStatusUpdate, setWsProgressStatusUpdate] = useState(0);
  const [errorFex, setErrorFex] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [graph, setGraph] = useState<any[]>([]);
  const [keys, setKeys] = useState<any[]>([]);
  const [fexStatus, setFexStatus] = useState(status);

  useSocket({
    callBack: (payload) => {
      if (parseInt(payload.status) === 100) {
        setShowResult(true);
        fetchFexStatus();
      }
      setWsProgressStatusUpdate(parseInt(payload.status));
    },
    channel: 'status',
    event: '.App\\Events\\EventProcessingFexStatus',
  });

  const startFexModule = async () => {
    setLoader(true);
    setErrorFex(false);
    try {
       await SheAiFexService.fex({
        evaluated_id: idEvaluated,
        debriefing_id: idDebriefing,
        priority: 0,
      })
      .then((response: any) => {
        if (response.status === 400) {
          //statusDataAi(idDebriefing);
          console.log(response)
        }
      })
      .catch((e: Error) => {});

      await fetchFexStatus();
    } catch (e) {
      setErrorFex(true);
      setLoader(false);
    }
  };

  const fetchFexStatus = async () => {
    setLoader(true);
    try {
      await SheAiFexService.fexStatus(idDebriefing)
      .then((response: any) => {
        if (response) {
          const data = response.data;
          if (data.length != 0 || data !== undefined) {
            setFexStatus(data?.status);
            if (data?.status == "finished") {
              setGraph(data.data.radar);
              setKeys(data.keys);
            }
            setLoader(false);
          } else {
            if (data.status) {
              setFexStatus(data?.status);
              setLoader(false);
            }
          }
        } else {
          setLoader(false);
        }
      })
      .catch((e: any) => {
        setLoader(false);
      });
    } catch (e) {
      setErrorFex(true);
    }
    setLoader(false);
  };

  useEffect(() => {
    // Ao montar o componente, busca o status do FEX
    fetchFexStatus();

  }, []);

  return (
    <>
      <Box className={styles.box} sx={{ mb: 10 }}>
        <TitleForm
          title={t("title_face_and_emotion_recognition")}
          icon={
            <EmojiEmotionsIcon
              sx={{ color: colors.purpleIcon, fontSize: 37, marginRight: 3 }}
            />
          }
          button={
            !errorFex &&
            (fexStatus == "not_found" ? (
              <Typography
                sx={{
                  ml: 4,
                  backgroundColor: colors.grayStatusDataIA,
                  paddingTop: 1,
                  paddingBottom: 1,
                  paddingLeft: 3,
                  paddingRight: 3,
                  borderRadius: 2,
                  color: colors.pink,
                  fontWeight: "bold",
                }}
              >
                {t("title_dialog_unprocessed")}
              </Typography>
            ) : fexStatus == "finished" ? (
              <Typography
                sx={{
                  ml: 4,
                  backgroundColor: colors.grayStatusDataIA,
                  paddingTop: 1,
                  paddingBottom: 1,
                  paddingLeft: 3,
                  paddingRight: 3,
                  borderRadius: 2,
                  color: colors.greenMedium,
                  fontWeight: "bold",
                }}
              >
                {t("title_dialog_processing_completed")}
              </Typography>
            ) : (
              <>
                <Typography
                  sx={{
                    ml: 4,
                    backgroundColor: colors.grayStatusDataIA,
                    paddingLeft: 3,
                    paddingRight: 3,
                    borderRadius: 2,
                    color: colors.purpleMediumSub,
                    fontWeight: "bold",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <span style={{ marginRight: "5px" }}>
                      {t("span_dialog_processing_simulation")}
                    </span>{" "}
                    <img src={require("../../../assets/images/load-status.gif")} />
                  </Box>
                </Typography>
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    color="secondary"
                    variant="determinate"
                    value={wsProgressStatusUpdate}
                  />
                  <Box
                    top={0}
                    left={0}
                    bottom={0}
                    right={0}
                    position="absolute"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography variant="caption" component="div" color="textPrimary">
                      {`${wsProgressStatusUpdate}%`}
                    </Typography>
                  </Box>
                </Box>
              </>
            ))
          }
        />
        <Typography className={styles.titleIa}>
          {t("first_title_dialog_description_data_ia")}
          &quot;{t("second_title_dialog_description_data_ia")}&quot;
          {t("third_title_dialog_description_data_ia")}
          &quot;{t("fourth_title_dialog_description_data_ia")}&quot;
          {t("fifth_title_dialog_description_data_ia")}
        </Typography>
        {/* Botões e alertas */}
        {!faceRecognition ? (
          <div>
                <Alert className={styles.alert} severity="error">
                  <AlertTitle>{t("first_title_alert_dialog_ia")}</AlertTitle>
                  {t("first_title_alert_description_data_ia")}
                   (<strong>{t("title_strong_fex")}</strong>)
                   {t("second_title_alert_description_data_ia")}
                  <List>
                    <ListItem>{t("first_title_list_item_data_ia")}</ListItem>
                    <ListItem>{t("second_title_list_item_data_ia")}
                    </ListItem>
                  </List>
                </Alert>
                <Alert className={styles.alert} severity="info">
                  <AlertTitle>{t("second_title_alert_dialog_ia")}</AlertTitle>
                  {t("third_title_alert_description_data_ia")} <strong>{t("first_title_strong_alert_description_data_ia")}</strong>
                  {t("fourth_title_alert_description_data_ia")}{" "}
                  <strong>{t("second_title_strong_alert_description_data_ia")}</strong>.
                </Alert>
              </div>
              ) : <></>
        }
        {!errorFex && fexStatus == "not_found" && (
            <Button
            variant="outlined"
            className={faceRecognition ? styles.buttonRF : styles.buttonDisabled}
            onClick={() => {
              if (faceRecognition !== null && faceRecognition !== undefined) {
                 startFexModule();
              }
            }}
            disabled={faceRecognition ? true : false}
            sx={{ display: secureLocalStorage.getItem('role') == 5 ? 'none' : 'block' }}
            >
            {t("btn_start_analysis")}
            </Button>
        )}
        {!errorFex && fexStatus == "finished" && (
          <Button variant="contained" onClick={() => setShowResult(true)}>
            {t("title_result")}
          </Button>
        )}
        {errorFex && <Typography>{t("title_description_error_sheai")}</Typography>}
      </Box>
      {showResult && (
        <Box className={styles.box}>
          <Grid container>
            <Grid item xs={11}>
              <Grid container spacing={0}>
                <Grid item sm={11}>
                  <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                    {t("title_face_and_emotion_recognition")}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={1}>
              <Button
                variant="outlined"
                onClick={() => setShowResult(false)}
                sx={{ borderWidth: 2 }}
              >
                {t("btn_close")} <CloseOutlinedIcon sx={{ ml: 1 }} />
              </Button>
            </Grid>
          </Grid>
          <Box className={styles.divider} sx={{ mt: 3 }}></Box>
          <Box sx={{p: 3}}>
            <DebriefingVideo
              video={video}
              heightVideo="500px"
              notMarker
              videoId={videoId}
              arrVideo={arrVideo}
              setVideo={setVideo}
              setVideoId={setVideoId}
              cameraMain={cameraMain}
              setCameraMain={setCameraMain}
            />
          </Box>
          <Typography sx={{ mt: 4, mb: 10 }}>
            {t("title_dialog_lifting_emotions")}
          </Typography>
          <Box sx={{ height: 500 }}>
            <ResponsiveRadar
              data={graph}
              keys={keys}
              indexBy="taste"
              valueFormat=">-.2f"
              margin={{ top: 70, right: 80, bottom: 40, left: 80 }}
              borderColor={{ from: "color" }}
              gridLevels={12}
              gridShape="circular"
              gridLabelOffset={36}
              dotSize={10}
              dotColor={{ theme: "background" }}
              dotBorderWidth={2}
              colors={{ scheme: "set2" }}
              blendMode="multiply"
              motionConfig="wobbly"
              legends={[
                {
                  anchor: 'top-left',
                  direction: 'column',
                  translateX: -50,
                  translateY: -40,
                  itemWidth: 180,
                  itemHeight: 40,
                  itemTextColor: '#000',
                  symbolSize: 15,
                  symbolShape: 'circle',
                  effects: [
                    {
                      on: 'hover',
                      style: {
                        itemTextColor: '#999',
                        symbolSize: 20,
                        symbolBorderWidth: 5
                      }
                    }
                  ]
                }
              ]}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default FexModule;
